<script setup>
import { onMounted, onUnmounted, watch, computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useIntervalFn, useIdle, useTimestamp } from '@vueuse/core'
import UserApi from "@api/user.api";
import { useUserStore } from "@store/user";
import { useMetadataStore } from "@store/metadata";

import NewSideBar from "@/views/layouts/partials/NewSideBar.vue";
import TopBar from "@/views/layouts/partials/TopBar.vue";
import TheFooter from "@/views/layouts/partials/TheFooter.vue";
import { useLoaderStore } from "@store/loader";

const route = useRoute();
const router = useRouter();
const loaderStore = useLoaderStore();

const isCollapse = ref(false)
const windowWidth = ref(0)

const handleOpen = (key, keyPath) => {
    // console.log('Menu opened:', key, keyPath)
}

const handleClose = (key, keyPath) => {
    // console.log('Menu closed:', key, keyPath)
}
onMounted(() => {
    // Load metadata before all children
    useMetadataStore().fetchData();

    // Set initial window width
    windowWidth.value = window.innerWidth;

    // Start with sidebar hidden only on mobile
    if (windowWidth.value <= 991) {
        isCollapse.value = true;
    } else {
        isCollapse.value = false;
    }

    const i = document.querySelector(".scroll-to-top");
    i && window.addEventListener("scroll", () => {
        const y = window.pageYOffset;
        i.style.display = y > 100 ? "block" : "none"
    })

    // Handle window resize
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

function handleResize() {
    windowWidth.value = window.innerWidth;
    if (windowWidth.value <= 991) {
        // On mobile, hide sidebar by default
        isCollapse.value = true;
    } else {
        // On desktop, show sidebar
        isCollapse.value = false;
    }
}

function toggleSidebar() {
    // Toggle sidebar visibility
    isCollapse.value = !isCollapse.value;
}

// Watch for changes in the collapsed state
watch(isCollapse, (newValue) => {
    // console.log('MasterLayout isCollapsed changed to:', newValue);
});

// ===========================================================

const now = useTimestamp({ interval: 1000 })
const { idle, lastActive } = useIdle(30 * 60 * 1000) // session time 30min

const idledFor = computed(() =>
    Math.floor((now.value - lastActive.value) / 1000))

watch(idle, (idleValue) => {
    if (idleValue) {

        useUserStore().logout()
            .then(() => {
                localStorage.clear();
                // router.push({ name: "login" });
                window.location.reload();
            })
            .catch((error) => {
                // console.log(error);
            })

    }
})

const sessionUpdateFrequency = 30; // time to update session time 30s

const { pause, resume, isActive } = useIntervalFn(() => {

    UserApi.postSessionTime({
        duration: sessionUpdateFrequency
    }).then(response => {
        const payload = response?.data;
        // console.log('session heartbeat payload:', payload);
        const shouldLogout = !!(payload && typeof payload === 'object' && payload.logout === true);
        if (shouldLogout) {
            useUserStore().logout()
                .then(() => {
                    localStorage.clear();
                    router.push({ name: "login" });
                })
                .catch(() => {
                    localStorage.clear();
                    router.push({ name: "login" });
                });
        }
    }).catch(error => {
        // console.error('Error updating session:', error);
    });

}, sessionUpdateFrequency * 1000)

const btnScrollToTop = document.querySelector(".scroll-to-top");

// toggle 'scroll to top' based on scroll position
window.addEventListener('scroll', e => {
    if (btnScrollToTop) {
        btnScrollToTop.style.display = window.scrollY > 20 ? 'block' : 'none';
    }
});
</script>

<template>
    <div
        v-if="false && loaderStore.isLoading && (route.path == '/projects' || route.path == '/documents')"
        class="me-2 text-center
        "
    >
        <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            width="170px"
            height="200px"
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid"
        >
            <circle
                cx="30"
                cy="50"
                fill="#fdb236"
                r="20"
            >
                <animate
                    attributeName="cx"
                    repeatCount="indefinite"
                    dur="1s"
                    keyTimes="0;0.5;1"
                    values="30;70;30"
                    begin="-0.5s"
                ></animate>
            </circle>
            <circle
                cx="70"
                cy="50"
                fill="#5e45ff"
                r="20"
            >
                <animate
                    attributeName="cx"
                    repeatCount="indefinite"
                    dur="1s"
                    keyTimes="0;0.5;1"
                    values="30;70;30"
                    begin="0s"
                ></animate>
            </circle>
            <circle
                cx="30"
                cy="50"
                fill="#fdb236"
                r="20"
            >
                <animate
                    attributeName="cx"
                    repeatCount="indefinite"
                    dur="1s"
                    keyTimes="0;0.5;1"
                    values="30;70;30"
                    begin="-0.5s"
                ></animate>
                <animate
                    attributeName="fill-opacity"
                    values="0;0;1;1"
                    calcMode="discrete"
                    keyTimes="0;0.499;0.5;1"
                    dur="1s"
                    repeatCount="indefinite"
                ></animate>
            </circle>
            <!-- [ldio] generated by https://loading.io/ -->
        </svg>
    </div>


    <div class="layout-container">
        <!-- Sidebar - hidden on mobile by default, shown when toggled -->
        <div
            class="sidebar-container"
            :class="[{ 'd-none d-lg-block': isCollapse && windowWidth <= 1024 }, isCollapse ? 'col-sm-1 mini-sidebar' : 'col-sm-2']"
            style="padding-right: 0; margin-right: 0;"
        >
            <new-side-bar
                :collapse="isCollapse"
                :handleOpen="handleOpen"
                :handleClose="handleClose"
                @toggle="toggleSidebar"
            />
        </div>

        <!-- Mobile sidebar overlay -->
        <!-- <div class="sidebar-container d-lg-none">
            <new-side-bar :collapse="isCollapse" :handleOpen="handleOpen" :handleClose="handleClose" @toggle="toggleSidebar" />
        </div> -->

        <!-- Main content area -->
        <div
            class="main-content"
            :class="isCollapse ? 'col-sm-11 col-xs-12 full-content' : 'col-sm-10 col-xs-12'"
            style="padding-left: 0; margin-left: 0;"
        >
            <top-bar
                :collapse="isCollapse"
                :handleOpen="handleOpen"
                :handleClose="handleClose"
                @toggle="toggleSidebar"
            />

            <router-view v-slot="{ Component, route }">
                <!-- Temporarily disabled transition to debug mounting issue -->
                <!-- <transition
                    :name="route.meta.transition || 'slide-fade'"
                    mode="out-in"
                > -->
                    <component
                        :is="Component"
                        :key="route.path"
                    />
                <!-- </transition> -->
            </router-view>
        </div>
    </div>

</template>

<style scoped>
.layout-container {
    min-height: 100vh;
    transition: all 0.3s ease;
}

/* Smooth transitions for sidebar toggle */
.sidebar-container,
.main-content {
    transition: all 0.3s ease;
}

/* Ensure content flows properly */
.col-sm-1,
.col-sm-2,
.col-sm-10,
.col-sm-11 {
    transition: all 0.3s ease;
}

/* Mobile layout adjustments */
@media (max-width: 991px) {
    .layout-container {
        display: block;
    }

    .main-content {
        width: 100%;
        flex: 0 0 100%;
        max-width: 100%;
    }

    .sidebar-container {
        position: fixed;
        top: 0;
        left: 0;
        z-index: 1050;
    }
}

/* Desktop layout */
@media (min-width: 992px) {
    .layout-container {
        display: flex;
        flex-wrap: wrap;
    }
}
</style>
